import gsap from 'gsap';
import barba from '@barba/core';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { preloadImages } from './animations/preloadImages';
import { initMenu } from './animations/menu';
import { initHeaderScroll } from './animations/header';
import { initGridParallax, initGridClick, initLatestGridClick } from './animations/grid';
import { initArticleScroll } from './animations/article';
import { initLoader } from './animations/initloader';
import { initScrambleLinks } from './animations/scramble';
import { initUrbanYellow } from './animations/yellow';
import { initBackToTop, hideBackToTop } from './animations/backToTop';
import { initAboutPage, getAboutScrollY } from './animations/about';
import { initHomeFlow, restoreHomeFlow } from './animations/home-flow';
import { uyTransitionIn, uyTransitionOut } from './animations/uy-transition';
import { initWelcomeLoader } from './animations/welcome-loader';
import { cleanupArticleClone } from './animations/article-transition';
import { initExplosion } from './animations/explosion';
import { init404Anim } from './animations/anim404';
import { curtainCover, loadWithProgress, curtainReveal, resetCurtain } from './animations/page-transition';

// ─────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────





const updateBodyClass = (data) => {
    const rawHTML   = data.next.html;
    const bodyMatch = /<body[^>]*class=["']([^"']*)["']/.exec(rawHTML);
    if (bodyMatch && bodyMatch[1]) {
        document.body.className = bodyMatch[1];
    }
};

const animateLoader = (show) => {
    const loader = document.querySelector('#transition-loader');
    if (!loader) return Promise.resolve();
    if (show) {
        gsap.set(loader, { autoAlpha: 0, display: 'flex' });
        return gsap.to(loader, { autoAlpha: 1, duration: 0.4, ease: 'power2.inOut' });
    } else {
        return gsap.to(loader, {
            autoAlpha: 0, duration: 0.4, ease: 'power2.inOut',
            onComplete: () => gsap.set(loader, { display: 'none' }),
        });
    }
};

const updatePanelTrigger = (namespace) => {
    const pt = document.querySelector('#panel-trigger');
    if (!pt) return;
    if (namespace === 'article') {
        gsap.set(pt, { display: 'flex', x: 0 });
    } else {
        gsap.set(pt, { display: 'none', x: 0 });
    }
};

const scrollToSection = (sectionId, lenis, savedScroll = 0) => {
    if (!sectionId) return;

    // Home : on revient au point exact du carrousel
    if (sectionId === 'home-stage' || sectionId === 'home-index') {
        restoreHomeFlow(savedScroll, lenis);
        return;
    }

    // Page Info : Workflow (ancre #workflow) et slides services (retour d'article)
    if (sectionId === 'workflow' || sectionId === 'hz-slide-01' || sectionId === 'hz-slide-02') {
        const y = getAboutScrollY(sectionId);
        if (y !== null) {
            window.scrollTo(0, y);
            lenis.scrollTo(y, { immediate: true, force: true });
        }
        return;
    }

    const target = document.querySelector('#' + sectionId);
    if (target) {
        window.scrollTo(0, target.getBoundingClientRect().top + window.scrollY);
        lenis.scrollTo(target, { immediate: true, force: true });
    }
};

const initScrollAnimations = (container, namespace, lenis) => {

    if (namespace === '404') init404Anim(container);

    if (namespace === 'front') initHomeFlow(container);

    if (container.querySelector('#grid-wrapper') || container.querySelector('#latest-grid')) {
        initGridParallax(container);
        initGridClick();
        initLatestGridClick();
    }

    if (namespace === 'urbanyellow') initUrbanYellow(container);
    if (namespace === 'article')     initArticleScroll(container, lenis);
};

const initGlobalAnimations = (container, lenis) => {
    initMenu(lenis);
    initHeaderScroll();
    initScrambleLinks();
    initExplosion((url) => barba.go(url));



    const namespace = container.getAttribute('data-barba-namespace');
    if (namespace === 'about') initAboutPage(container);

    return namespace;
};

// Le rideau (ouverture par le logo) sert à TOUTES les sorties de page, sauf trois entrées spécifiques :
// vers un article (l'image s'agrandit), vers Urban Yellow (volet jaune) et le bouton « explosion ».
// Pour ces transitions « rideau », le nettoyage (scroll, ScrollTrigger) se fait APRÈS que le rideau
// a couvert la page : plus de saut visible de l'ancienne page.
const isStandardTransition = (data) => {
    const to = data?.next?.namespace;
    if (to === 'article' || to === 'urbanyellow') return false;
    if (data?.trigger?.classList?.contains?.('explosion')) return false;
    if (sessionStorage.getItem('transition_type') === 'article') return false;
    return true;
};

// Ancre visée par le lien cliqué (Barba retire le #hash de data.next.url)
const getTargetHash = (data) => {
    const href = data?.trigger?.href || data?.trigger?.getAttribute?.('href') || '';
    try { return new URL(href, window.location.href).hash.slice(1); } catch { return ''; }
};

const killPageTriggers = () => {
    ScrollTrigger.getAll()
        .filter(st => st.vars.id !== 'back-to-top')
        .forEach(st => st.kill());
};

const resetScroll = (lenis) => {
    window.scrollTo(0, 0);
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
    lenis.scrollTo(0, { immediate: true, force: true });
};

// ─────────────────────────────────────────────
// Barba
// ─────────────────────────────────────────────

export const initBarba = (lenis) => {

    // Filet de sécurité « Précédent / Suivant » : si l'entrée d'historique n'a pas été créée par Barba
    // (état écrasé par un autre script, ou absent), Barba plante et la page ne change plus.
    // On intercepte alors l'événement AVANT Barba et on recharge simplement l'adresse demandée.
    window.addEventListener('popstate', (e) => {
        const st = e.state;
        if (!st || st.from !== 'barba' || !Array.isArray(st.states)) {
            e.stopImmediatePropagation();
            window.location.reload();
        }
    });


    // Entrée d'une page derrière le rideau (transition standard, sortie d'article, sortie d'Urban Yellow)
    //   restore : pour la sortie d'article, retour à la section / position de scroll d'origine
    const curtainEnter = async (data, { restore = false } = {}) => {
        updateBodyClass(data);
        gsap.set(data.next.container, { opacity: 0 });

        // 1. On attend que le rideau couvre l'écran (même promesse que leave)
        await curtainCover(data);

        // 2. Nettoyage de l'ancienne page, invisible sous le rideau
        gsap.killTweensOf(data.current.container);
        gsap.set(data.current.container, { display: 'none' });
        killPageTriggers();
        resetScroll(lenis);

        const sectionId   = restore ? (sessionStorage.getItem('transition_section') || '') : '';
        const savedScroll = restore ? (parseFloat(sessionStorage.getItem('transition_scroll')) || 0) : 0;

        // 3. Chargement des images (trait de progression sous le nom de la page)
        await loadWithProgress(data.next.container);

        const namespace = initGlobalAnimations(data.next.container, lenis);
        initScrollAnimations(data.next.container, namespace, lenis);
        updatePanelTrigger(namespace);

        if (restore) {
            ['transition_type', 'transition_image', 'transition_referer', 'transition_scroll', 'transition_section']
                .forEach((k) => sessionStorage.removeItem(k));
        }

        lenis.resize();
        ScrollTrigger.refresh(true);
        if (restore) scrollToSection(sectionId, lenis, savedScroll);
        else { const hash = getTargetHash(data); if (hash) scrollToSection(hash, lenis); }

        // 4. Ouverture par le logo (le scroll est rendu tout de suite)
        lenis.start();
        await curtainReveal(data.next.container);
        ScrollTrigger.refresh();
    };

    barba.hooks.before((data) => {
        lenis.stop();
        hideBackToTop();
        resetCurtain();

        // Transition standard : on laisse l'ancienne page intacte sous le rideau,
        // le nettoyage se fait dans enter() une fois la page couverte
        if (isStandardTransition(data)) {
            cleanupArticleClone();
            return;
        }

        gsap.killTweensOf('*'); // ← killer tous les tweens actifs


        // Cleanup clone orphelin — sauf vers article où enter() en a besoin
        if (data?.next?.namespace !== 'article') cleanupArticleClone();

        // Kill tous les ST sauf back-to-top
        ScrollTrigger.getAll()
            .filter(st => st.vars.id !== 'back-to-top')
            .forEach(st => st.kill());

        // Reset scroll pour toutes les transitions SAUF from-article
        if (data?.current?.namespace !== 'article') {
            resetScroll(lenis);
        }
    });

    barba.init({
        sync: true,
        // Délai max de téléchargement d'une page. Par défaut Barba abandonne au bout de 2 s
        // et recharge toute la page (écran blanc + loader) : sur connexion lente, la transition
        // était coupée net. 15 s laisse le temps à la page d'arriver sous l'image agrandie.
        timeout: 15000,
        transitions: [

            // ── Retour depuis article ──
            {
                name: 'from-article',
                from: { namespace: ['article'] },

                async leave(data) {
                    const pt = document.querySelector('#panel-trigger');
                    gsap.set(document.querySelector('header'), { clearProps: 'marginLeft' });
                    gsap.set(document.querySelector('footer'), { clearProps: 'marginLeft' });
                    if (pt) gsap.set(pt, { x: 0, display: 'none' });

                    // Vers Urban Yellow : son propre volet ; sinon le rideau
                    if (!isStandardTransition(data)) return Promise.resolve();
                    return curtainCover(data);
                },

                async enter(data) {
                    // Retour à la page d'origine, à la même section / position
                    await curtainEnter(data, { restore: true });
                },
            },

            // ── Vers Urban Yellow ──
            {
                name: 'to-urbanyellow',
                to: { namespace: ['urbanyellow'] },

                async leave({ current }) {
                    await uyTransitionIn();
                    return gsap.to(current.container, { opacity: 0, duration: 0.2 });
                },

                async enter({ next }) {
                    updateBodyClass({ next });
                    gsap.set(next.container, { opacity: 1 });
                    const namespace = initGlobalAnimations(next.container, lenis);
                    initScrollAnimations(next.container, namespace, lenis);
                    updatePanelTrigger(namespace);
                    await uyTransitionOut();
                    requestAnimationFrame(() => {
                        requestAnimationFrame(() => {
                            lenis.resize();
                            lenis.start();
                            ScrollTrigger.refresh(true);
                        });
                    });
                },
            },

            // ── Depuis Urban Yellow ──
            {
                name: 'from-urbanyellow',
                from: { namespace: ['urbanyellow'] },

                async leave(data) {
                    return curtainCover(data);
                },

                async enter(data) {
                    await curtainEnter(data);
                },
            },


            // ── Transition explosion ──
{
    name: 'explosion-transition',
    from: { custom: ({ trigger }) => trigger?.classList?.contains('explosion') },

    async leave(data) {
        // Le cercle est déjà en train d'expand via initExplosion
        // On attend qu'il soit plein écran (0.7s) puis on masque le container
        return new Promise(resolve => {
            gsap.to(data.current.container, { 
                opacity: 0, 
                duration: 0.3, 
                delay: 0.4,
                onComplete: resolve 
            });
        });
    },

    async enter(data) {
        updateBodyClass(data);
        gsap.set(data.next.container, { opacity: 0 });

        await preloadImages(data.next.container);

        const namespace = initGlobalAnimations(data.next.container, lenis);
        initScrollAnimations(data.next.container, namespace, lenis);
        updatePanelTrigger(namespace);

        // Fade in après que le cercle ait disparu
        requestAnimationFrame(() => requestAnimationFrame(() => {
            lenis.resize();
            lenis.start();
            ScrollTrigger.refresh(true);
            gsap.to(data.next.container, { opacity: 1, duration: 0.5, ease: 'power2.out' });
        }));
    },
},










            // ── Transition universelle ──
            {
                name: 'universal-transition',

                async once(data) {
                    const ns = data.next.container.getAttribute('data-barba-namespace');
                    updatePanelTrigger(ns);

                    const params    = new URLSearchParams(window.location.search);
                    const fromLogin = params.get('from_login');
                    const userName  = params.get('user') || '';
                    const initialHash = window.location.hash.slice(1);

                    // Rechargement (F5) en cours de page : on rétablit la position, sans le vol des lettres
                    // (il n'a de sens qu'en haut de la home). En haut de page, l'intro complète est jouée.
                    const reloadY = (() => {
                        try {
                            const nav = performance.getEntriesByType('navigation')[0];
                            if (!nav || nav.type !== 'reload') return null;
                            const saved = JSON.parse(sessionStorage.getItem('cs_reload') || 'null');
                            return saved && saved.path === window.location.pathname && saved.y > window.innerHeight * 0.5 ? saved.y : null;
                        } catch (e) { return null; }
                    })();
                    // Nettoie l'URL (?from_login, #ancre) en GARDANT l'état d'historique posé par Barba :
                    // le remplacer par {} cassait le bouton « Précédent » du navigateur vers cette page
                    if (window.location.search || window.location.hash) {
                        window.history.replaceState(window.history.state, '', window.location.pathname);
                    }

                    const siteInit = async () => {
                        // ✅ Preload pendant que l'initLoader overlay est visible
                        await preloadImages(data.next.container);
                        const namespace = initGlobalAnimations(data.next.container, lenis);
                        initScrollAnimations(data.next.container, namespace, lenis);
                        initBackToTop(lenis);
                        lenis.start();
                        ScrollTrigger.refresh();
                        // Arrivée directe sur une ancre (ex. /info/#workflow)
                        if (initialHash) scrollToSection(initialHash, lenis);
                        // Rechargement : retour à la position d'avant
                        else if (reloadY !== null) {
                            // Lenis garde en cache la hauteur de page d'avant le pin de la home :
                            // sans resize, il plafonnerait le scroll à la fin du split
                            lenis.resize();
                            if (namespace === 'front') restoreHomeFlow(reloadY, lenis);
                            else {
                                window.scrollTo(0, reloadY);
                                lenis.scrollTo(reloadY, { immediate: true, force: true });
                            }
                        }
                    };

                    if (fromLogin && userName) {
                        const mainLoader = document.getElementById('loader-overlay');
                        const loaderBg   = document.getElementById('loader-bg');
                        if (mainLoader) gsap.set(mainLoader, { display: 'none' });
                        if (loaderBg)   gsap.set(loaderBg,   { display: 'none' });

                        await initWelcomeLoader(userName, async () => {
                            const namespace = initGlobalAnimations(data.next.container, lenis);
                            initScrollAnimations(data.next.container, namespace, lenis);
                            initBackToTop(lenis);
                            lenis.start();
                            ScrollTrigger.refresh();
                        });
                    } else {
                        await initLoader(siteInit, { plainExit: reloadY !== null });
                    }

                    if (ns === 'urbanyellow') {
                        window.addEventListener('load', () => ScrollTrigger.refresh(true), { once: true });
                    }
                },

                async leave(data) {
                    const storageType = sessionStorage.getItem('transition_type');
                    const isToArticle = storageType === 'article' || data.next.namespace === 'article';
                    if (isToArticle) return Promise.resolve();

                    // Rideau : la même promesse est attendue par enter() (mode sync)
                    return curtainCover(data);
                },

                async enter(data) {
                    await curtainEnter(data);
                },
            },

// ── Vers article ──
{
    name: 'to-article',
    to: { namespace: ['article'] },

    async leave() {
        return Promise.resolve();
    },

    async enter({ next }) {
        updateBodyClass({ next });
        window.scrollTo(0, 0);
        lenis.scrollTo(0, { immediate: true, force: true });

        gsap.set(next.container, { opacity: 0 });

        // Lance le preload en parallèle dès le début
        const preloadPromise = preloadImages(next.container);

        const namespace = initGlobalAnimations(next.container, lenis);
        initScrollAnimations(next.container, namespace, lenis);
        updatePanelTrigger(namespace);

        const clone = document.querySelector('img[style*="z-index: 9999"]');

        if (clone) {
            // Container visible — le clone couvre tout, rien ne flashe
            gsap.set(next.container, { opacity: 1 });

            sessionStorage.removeItem('transition_image');
            sessionStorage.removeItem('transition_type');

            // Clone est le seul masque — on attend que la page soit prête dessous
            await preloadPromise;

            // Page prête — on fade le clone
            gsap.set(clone, { willChange: 'auto', opacity: 1 });
            const tween = gsap.to(clone, {
                opacity: 0,
                duration: 0.8,
                ease: 'expo.out',
                onComplete: () => clone.remove(),
            });

        } else {
            // Pas de clone — on attend avant de révéler pour éviter tout flash
            await preloadPromise;
            gsap.set(next.container, { opacity: 1 });
        }

        requestAnimationFrame(() => requestAnimationFrame(() => {
            lenis.resize();
            lenis.start();
            ScrollTrigger.refresh(true);
        }));
    },
},
        ],
    });
};